import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Button,
  FormFieldText,
  FormFieldPassword,
  ErrorMessageList,
} from '@/components/ui'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config'
import { useEmployeeLogin } from '@/hooks/features/auth/employeeLogin'
import { invalidateUser } from '@/hooks/features/user'
import i18n from '@/lib/i18n'
import { storage } from '@/utils/storage'
import { getErrorMessages } from '@/utils/text'

import { schema, formLabels } from './const'
import { EmployeeAuthLoginFormProps, EmployeeLoginFormVO } from './type'
import { generateDefaultValues } from './util'

export const EmployeeAuthLoginForm = ({
  onSuccess,
  onChangeErrorMessage,
}: EmployeeAuthLoginFormProps) => {
  const { t } = useTranslation()
  const [errorMessages] = useState<string[]>([])

  const { isLoading, mutate } = useEmployeeLogin({}) // Swap if sharing the standard useLogin hook

  const { register, control, formState, handleSubmit } =
    useForm<EmployeeLoginFormVO>({
      resolver: schema && zodResolver(schema),
      shouldUnregister: true,
      defaultValues: generateDefaultValues(),
    })

  const onSubmit = useCallback(
    async ({ userId, password }: EmployeeLoginFormVO) => {
      onChangeErrorMessage([])
      mutate(
        { data: { userId, password } },
        {
          onSuccess: async ({ accessToken, refreshToken }) => {
            storage.set(ACCESS_TOKEN_KEY, accessToken)
            storage.set(REFRESH_TOKEN_KEY, refreshToken)
            await i18n.changeLanguage('ja')

            // Note: ユーザー情報更新
            await invalidateUser()

            onSuccess()
          },
          onError: (error) => {
            if (error.response?.status === 400) {
              onChangeErrorMessage(
                getErrorMessages({
                  fields: error.response.data.fields,
                  fieldNames: formLabels,
                })
              )
            }
            if (error.response?.status === 401) {
              onChangeErrorMessage(['ログインに失敗しました。'])
            }
          },
        }
      )
    },
    [onChangeErrorMessage, mutate, onSuccess]
  )

  return (
    <div className={clsx('tw-w-full')}>
      {errorMessages.length > 0 && (
        <div className={clsx('tw-mb-2 tw-text-center')}>
          <ErrorMessageList errorMessages={errorMessages} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={clsx('tw-mt-2')}>
        <FormFieldText
          type="text"
          label={t('auth.login.userId')}
          required
          error={formState.errors['userId']}
          registration={register('userId')}
        />
        <FormFieldPassword
          label={t('auth.login.password')}
          required
          name="password"
          error={formState.errors['password']}
          control={control}
        />

        <div className={clsx('tw-mt-12')}>
          <Button
            type="submit"
            isLoading={isLoading}
            className={clsx('tw-w-full')}
            variant="primary"
          >
            {t('auth.login.submit')}
          </Button>
        </div>
      </form>
    </div>
  )
}
